import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { categories, ranking } from "@/mocks/siteData";

const navLinks = [
  { label: "首页", to: "/" },
  { label: "事件分类", to: "/category", hasMenu: true },
  { label: "名场面回放", to: "/category?cat=classic-moments" },
  { label: "关于我们", to: "/about" },
  { label: "常见问题", to: "/faq" },
  { label: "联系我们", to: "/contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setMenuOpen(false);
    setSearchOpen(false);
  }, [location.pathname, location.search]);

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    const value = keyword.trim();
    navigate(value ? `/category?q=${encodeURIComponent(value)}` : "/category");
    setSearchOpen(false);
  };

  const tickerItems = [...ranking, ...ranking];

  return (
    <header className="sticky top-0 z-50 w-full">
      <div className="bg-foreground-950 text-background-50 overflow-hidden">
        <div className="flex items-center h-9">
          <span className="shrink-0 flex items-center gap-1.5 pl-4 pr-3 h-full bg-primary-600 text-background-50 text-xs font-medium tracking-wide whitespace-nowrap">
            <i className="ri-fire-line"></i>
            翻车热榜
          </span>
          <div className="relative flex-1 overflow-hidden">
            <div className="marquee-track flex w-max items-center">
              {tickerItems.map((item, index) => (
                <Link
                  key={`${item.id}-${index}`}
                  to={`/article/${item.id}`}
                  className="flex items-center gap-2 px-5 text-xs text-background-200 hover:text-accent-400 transition-colors whitespace-nowrap"
                >
                  <span className="text-primary-400 font-semibold">{String(item.rank).padStart(2, "0")}</span>
                  {item.title}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div
        className={`w-full border-b transition-colors duration-300 ${
          scrolled
            ? "bg-background-50/95 backdrop-blur-md border-background-200"
            : "bg-background-50 border-transparent"
        }`}
      >
        <div className="w-full px-4 md:px-8 h-16 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2.5 shrink-0 group">
            <span className="w-9 h-9 rounded-md bg-primary-500 text-background-50 flex items-center justify-center">
              <i className="ri-alarm-warning-line text-lg"></i>
            </span>
            <span className="flex flex-col leading-none">
              <span className="font-heading font-bold text-lg md:text-xl text-foreground-950 tracking-tight">
                直播翻车观察局
              </span>
              <span className="hidden sm:block text-[11px] text-foreground-500 mt-1 tracking-wide">
                网红翻车现场 · 事件复盘
              </span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-1" aria-label="主导航">
            {navLinks.map((link) =>
              link.hasMenu ? (
                <div
                  key={link.to}
                  className="relative"
                  onMouseEnter={() => setMenuOpen(true)}
                  onMouseLeave={() => setMenuOpen(false)}
                >
                  <Link
                    to={link.to}
                    className="flex items-center gap-1 px-3 py-2 text-sm text-foreground-700 hover:text-primary-600 transition-colors whitespace-nowrap"
                  >
                    {link.label}
                    <i className="ri-arrow-down-s-line text-base"></i>
                  </Link>
                  {menuOpen && (
                    <div className="absolute left-0 top-full pt-2 w-[520px]">
                      <div className="bg-background-50 border border-background-200 rounded-lg p-3 grid grid-cols-2 gap-1">
                        {categories.map((cat) => (
                          <Link
                            key={cat.slug}
                            to={`/category?cat=${cat.slug}`}
                            className="flex items-start gap-2.5 p-2.5 rounded-md hover:bg-background-100 transition-colors"
                          >
                            <span className="w-8 h-8 shrink-0 rounded-md bg-secondary-100 text-secondary-900 flex items-center justify-center">
                              <i className={`${cat.icon} text-base`}></i>
                            </span>
                            <span className="flex flex-col">
                              <span className="text-sm font-medium text-foreground-950">{cat.name}</span>
                              <span className="text-xs text-foreground-500 line-clamp-1">{cat.desc}</span>
                            </span>
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={link.to}
                  to={link.to}
                  className="px-3 py-2 text-sm text-foreground-700 hover:text-primary-600 transition-colors whitespace-nowrap"
                >
                  {link.label}
                </Link>
              )
            )}
          </nav>

          <div className="flex items-center gap-2">
            <button
              type="button"
              aria-label="搜索"
              onClick={() => setSearchOpen((prev) => !prev)}
              className="w-9 h-9 rounded-md border border-background-300 text-foreground-700 flex items-center justify-center hover:border-primary-400 hover:text-primary-600 transition-colors cursor-pointer"
            >
              <i className={searchOpen ? "ri-close-line" : "ri-search-line"}></i>
            </button>
            <Link
              to="/contact"
              className="hidden md:inline-flex items-center gap-1.5 px-4 h-9 rounded-md bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors"
            >
              <i className="ri-send-plane-line"></i>
              提供线索
            </Link>
            <button
              type="button"
              aria-label="移动端菜单"
              onClick={() => setMobileOpen((prev) => !prev)}
              className="lg:hidden w-9 h-9 rounded-md border border-background-300 text-foreground-800 flex items-center justify-center cursor-pointer"
            >
              <i className={mobileOpen ? "ri-close-line text-lg" : "ri-menu-line text-lg"}></i>
            </button>
          </div>
        </div>

        {searchOpen && (
          <div className="border-t border-background-200 bg-background-100">
            <form onSubmit={submitSearch} className="w-full px-4 md:px-8 py-3 flex items-center gap-2">
              <span className="w-9 h-9 shrink-0 rounded-md bg-background-50 border border-background-300 flex items-center justify-center text-foreground-500">
                <i className="ri-search-line"></i>
              </span>
              <input
                type="search"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索事件、主播或关键词，例如：忘关麦、报价失误"
                className="flex-1 h-9 px-3 text-sm rounded-md bg-background-50 border border-background-300 text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400"
              />
              <button
                type="submit"
                className="h-9 px-4 rounded-md bg-foreground-950 text-background-50 text-sm whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
              >
                搜索
              </button>
            </form>
          </div>
        )}

        {mobileOpen && (
          <div className="lg:hidden border-t border-background-200 bg-background-50">
            <nav className="px-4 py-3 flex flex-col" aria-label="移动端导航">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className="px-1 py-3 text-sm text-foreground-800 border-b border-background-200 last:border-b-0 hover:text-primary-600 transition-colors"
                >
                  {link.label}
                </Link>
              ))}
              <div className="pt-3">
                <p className="text-xs text-foreground-500 mb-2">按分类浏览</p>
                <div className="flex flex-wrap gap-2">
                  {categories.map((cat) => (
                    <Link
                      key={cat.slug}
                      to={`/category?cat=${cat.slug}`}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-background-100 border border-background-200 text-xs text-foreground-700"
                    >
                      <i className={cat.icon}></i>
                      {cat.name}
                    </Link>
                  ))}
                </div>
              </div>
              <Link
                to="/contact"
                className="mt-4 inline-flex items-center justify-center gap-1.5 h-10 rounded-md bg-primary-500 text-background-50 text-sm font-medium"
              >
                <i className="ri-send-plane-line"></i>
                提供线索
              </Link>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}